import { BASE_WS_URL } from "@infrastructure/clients/http/config";
import { AuthService } from "@domain/services/auth.service";
import { OrderStore } from "@infrastructure/stores/order.store";
import { OrderStatus } from "@domain/enums/order.enums";
import { OrderHistoryListItem } from "@domain/dtos/models/order.models";

export interface UserWebSocketMessage {
  type: string;
  subtype?: string; // Для type: "orders" может быть "active" или "error"
  orders?: OrderHistoryListItem[];
  order?: OrderHistoryListItem;
  order_number?: string;
  status?: string;
  cell_number?: number;
  error?: string;
  timestamp?: string;
  [key: string]: any;
}

/**
 * WebSocket клиент для получения всех уведомлений пользователя
 * Подключается к /ws/user/{userId} и получает все типы уведомлений (заказы, платежи, статусы и т.д.)
 */
export class UserWebSocketClient {
  private ws: WebSocket | null = null;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private reconnectAttempts = 0;
  private currentUserId: number | null = null;
  private readonly MAX_RECONNECT_ATTEMPTS = 5;
  private readonly RECONNECT_DELAY_MS = 3000;

  /**
   * Подключается к WebSocket для пользователя
   */
  async connectToUser(userId: number): Promise<void> {
    // Закрываем существующее соединение если есть
    this.disconnect();

    const token = AuthService.getToken();
    if (!token) {
      throw new Error("Cannot connect WebSocket: no token");
    }

    this.currentUserId = userId;
    const wsUrl = `${BASE_WS_URL}/ws/user/${userId}?token=${token}`;

    return new Promise((resolve, reject) => {
      this.ws = new WebSocket(wsUrl);

      let connectionFailed = false;

      this.ws.onopen = () => {
        console.log("[UserWebSocketClient] Connected to WebSocket for user", userId);
        this.reconnectAttempts = 0;
        resolve();
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data) as UserWebSocketMessage;
          console.log("[UserWebSocketClient] WebSocket message received:", data);
          this.handleMessage(data);
        } catch (error) {
          console.error("[UserWebSocketClient] Error parsing WebSocket message:", error);
        }
      };

      this.ws.onerror = (error) => {
        console.error("[UserWebSocketClient] WebSocket error:", error);
        console.error(`[UserWebSocketClient] WebSocket URL: ${wsUrl}`);
        connectionFailed = true;
        // Не реджектим промис здесь, так как onclose может дать больше информации
      };

      this.ws.onclose = (event) => {
        console.log(`[UserWebSocketClient] WebSocket closed: code=${event.code}, reason=${event.reason || 'none'}, wasClean=${event.wasClean}`);
        console.log(`[UserWebSocketClient] WebSocket URL was: ${wsUrl}`);
        this.ws = null;

        // Код 1006 (abnormal closure) часто означает, что сервер не ответил на handshake
        // Это может быть 404, но браузер не передает точный код
        // Если соединение не было установлено (wasClean=false) и код 1006, вероятно это 404
        const isPermanentError = 
          event.code === 404 || 
          event.code === 4001 || 
          (event.code === 1006 && !event.wasClean && connectionFailed);

        if (isPermanentError) {
          console.error(
            `[UserWebSocketClient] Permanent error detected (code=${event.code}, wasClean=${event.wasClean}), ` +
            `stopping reconnection attempts. Check if WebSocket endpoint exists on backend.`
          );
          this.reconnectAttempts = this.MAX_RECONNECT_ATTEMPTS; // Останавливаем попытки
          if (!event.wasClean) {
            reject(new Error(`WebSocket connection failed: code=${event.code}, reason=${event.reason || 'Connection closed abnormally'}`));
          }
          return;
        }

        // Автоматическое переподключение если пользователь авторизован
        import("@infrastructure/stores/auth.store").then(({ AuthStore }) => {
          const authState = AuthStore.auth;
          if (authState.state === "authenticated" && authState.user?.id === userId) {
            this.scheduleReconnect(userId);
          }
        });
      };
    });
  }

  /**
   * Обрабатывает сообщения от WebSocket
   */
  private handleMessage(data: UserWebSocketMessage): void {
    const messageType = data.type;
    const subtype = (data as any).subtype;

    // Обработка топика orders с подтипами active и error
    if (messageType === "orders") {
      switch (subtype) {
        case "active":
          // При подключении или обновлении списка активных заказов
          if (data.orders && Array.isArray(data.orders)) {
            console.log(`[UserWebSocketClient] Received active orders list: ${data.orders.length} orders`);
            OrderStore.setActiveOrders(data.orders);
          }
          break;

        case "error":
          // Ошибка заказа
          if (data.order_number) {
            console.error(`[UserWebSocketClient] Order error: ${data.order_number}, error: ${data.error}`);
            OrderStore.updateOrderStatus(data.order_number, OrderStatus.ERROR);
          }
          break;

        default:
          console.warn(`[UserWebSocketClient] Unknown orders subtype: ${subtype}`, data);
      }
      return;
    }

    // Обработка других типов сообщений (для обратной совместимости)
    switch (messageType) {
      case "active_orders":
        // Обратная совместимость со старым форматом
        if (data.orders && Array.isArray(data.orders)) {
          console.log(`[UserWebSocketClient] Received active orders list (legacy format): ${data.orders.length} orders`);
          OrderStore.setActiveOrders(data.orders);
        }
        break;

      case "order_error":
        // Обратная совместимость со старым форматом
        if (data.order_number) {
          console.error(`[UserWebSocketClient] Order error (legacy format): ${data.order_number}, error: ${data.error}`);
          OrderStore.updateOrderStatus(data.order_number, OrderStatus.ERROR);
        }
        break;

      case "feedback_request":
        // Команда на открытие модалки обратной связи
        if (data.order_number) {
          console.log(`[UserWebSocketClient] Feedback request received for order: ${data.order_number}`);
          // Отправляем событие для OrderFeedbackManager
          window.dispatchEvent(
            new CustomEvent("feedback_request", { detail: { order_number: data.order_number } })
          );
        }
        break;

      case "close_cell_request":
        // Команда на открытие модалки с просьбой закрыть ячейку
        if (data.order_number) {
          console.log(`[UserWebSocketClient] Close cell request received for order: ${data.order_number}`);
          // Отправляем событие для CloseCellModalManager
          window.dispatchEvent(
            new CustomEvent("close_cell_request", {
              detail: {
                order_number: data.order_number,
                machine_id: data.machine_id,
                cell_id: data.cell_id,
                message: data.message,
              },
            })
          );
        }
        break;

      default:
        console.warn(`[UserWebSocketClient] Unknown WebSocket message type: ${messageType}`, data);
    }
  }

  /**
   * Планирует переподключение WebSocket
   */
  private scheduleReconnect(userId: number): void {
    if (this.reconnectAttempts >= this.MAX_RECONNECT_ATTEMPTS) {
      console.error("[UserWebSocketClient] Max reconnect attempts reached, stopping");
      return;
    }

    this.reconnectAttempts++;
    const delay = this.RECONNECT_DELAY_MS * this.reconnectAttempts;

    console.log(
      `[UserWebSocketClient] Scheduling WebSocket reconnect attempt ${this.reconnectAttempts}/${this.MAX_RECONNECT_ATTEMPTS} in ${delay}ms`
    );

    this.reconnectTimeout = setTimeout(() => {
      this.connectToUser(userId).catch((error) => {
        console.error("[UserWebSocketClient] Reconnect failed:", error);
      });
    }, delay);
  }

  /**
   * Закрывает WebSocket соединение
   */
  disconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.reconnectAttempts = 0;
    this.currentUserId = null;
  }

  /**
   * Проверяет, подключено ли соединение
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}

// Singleton instance
export const userWebSocketClient = new UserWebSocketClient();

